import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { adminAPI, employeeAPI, branchAPI, leaveTypeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, FileText, AlertCircle } from 'lucide-react';

export default function ManualEntryPage() {
  const { isSuperUser } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [manualAttendance, setManualAttendance] = useState([]);
  const [manualLeaves, setManualLeaves] = useState([]);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: '', branch_id: '', date: '',
    check_in_time_local: '09:30', check_out_time_local: '18:00',
    is_late: false, late_minutes: 0, is_half_day: false, notes: ''
  });

  const [leaveForm, setLeaveForm] = useState({
    employee_id: '', branch_id: '', leave_type_id: '',
    start_date: '', end_date: '', days: 1,
    reason: '', is_half_day: false, notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadLeaveTypes();
      loadManualRecords();
    }
  }, [selectedBranch]);

  const loadData = async () => {
    try {
      const [empRes, branchRes] = await Promise.all([
        employeeAPI.list(),
        branchAPI.list()
      ]);
      setEmployees(empRes.data);
      setBranches(branchRes.data);
      if (branchRes.data.length > 0) {
        setSelectedBranch(branchRes.data[0].id);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const res = await leaveTypeAPI.list(selectedBranch);
      setLeaveTypes(res.data);
    } catch (e) { console.error(e); }
  };

  const loadManualRecords = async () => {
    try {
      const [attRes, leaveRes] = await Promise.all([
        adminAPI.listManualAttendance({ branch_id: selectedBranch }),
        adminAPI.listManualLeaves({ branch_id: selectedBranch })
      ]);
      setManualAttendance(attRes.data);
      setManualLeaves(leaveRes.data);
    } catch (e) { console.error(e); }
  };

  const branchEmployees = employees.filter(e => e.branch_id === selectedBranch);
  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || 'Unknown';

  const handleCreateAttendance = async () => {
    if (!attendanceForm.employee_id || !attendanceForm.date) {
      toast.error('Employee and date are required');
      return;
    }
    try {
      const payload = { ...attendanceForm, branch_id: selectedBranch };
      await adminAPI.createManualAttendance(payload);
      toast.success('Manual attendance record created');
      setAttendanceDialogOpen(false);
      loadManualRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleDeleteAttendance = async (id) => {
    try {
      await adminAPI.deleteManualAttendance(id);
      toast.success('Record deleted');
      loadManualRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateLeave = async () => {
    if (!leaveForm.employee_id || !leaveForm.start_date || !leaveForm.leave_type_id) {
      toast.error('Employee, leave type, and start date are required');
      return;
    }
    try {
      const payload = { ...leaveForm, branch_id: selectedBranch };
      await adminAPI.createManualLeave(payload);
      toast.success('Manual leave record created');
      setLeaveDialogOpen(false);
      loadManualRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create leave');
    }
  };

  const handleDeleteLeave = async (id) => {
    try {
      await adminAPI.deleteManualLeave(id);
      toast.success('Leave record deleted');
      loadManualRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openAttendanceDialog = () => {
    setAttendanceForm({
      employee_id: branchEmployees[0]?.id || '', branch_id: selectedBranch,
      date: '', check_in_time_local: '09:30', check_out_time_local: '18:00',
      is_late: false, late_minutes: 0, is_half_day: false, notes: ''
    });
    setAttendanceDialogOpen(true);
  };

  const openLeaveDialog = () => {
    setLeaveForm({
      employee_id: branchEmployees[0]?.id || '', branch_id: selectedBranch,
      leave_type_id: leaveTypes[0]?.id || '',
      start_date: '', end_date: '', days: 1,
      reason: '', is_half_day: false, notes: ''
    });
    setLeaveDialogOpen(true);
  };

  if (!isSuperUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-muted-foreground" size={40} />
          <p className="text-muted-foreground">Only Super Users can access manual data entry.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="h-32 bg-muted animate-pulse rounded-xl" />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight" data-testid="manual-entry-title">Manual Data Entry</h1>
          <p className="text-muted-foreground">Backdate attendance and leave records for historical data</p>
        </div>
        {branches.length > 0 && (
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[220px]" data-testid="manual-entry-branch-select">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="attendance" className="w-full">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="manual-attendance-tab">
            <Clock size={14} className="mr-2" /> Attendance
          </TabsTrigger>
          <TabsTrigger value="leaves" data-testid="manual-leaves-tab">
            <FileText size={14} className="mr-2" /> Leaves
          </TabsTrigger>
        </TabsList>

        {/* ATTENDANCE TAB */}
        <TabsContent value="attendance" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {manualAttendance.length} manual attendance record{manualAttendance.length !== 1 ? 's' : ''}
            </p>
            <Button onClick={openAttendanceDialog} className="gap-2" data-testid="add-manual-attendance-button">
              <Plus size={16} /> Add Attendance Record
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {manualAttendance.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="mx-auto mb-3 text-muted-foreground/50" size={40} />
                  <p>No manual attendance records yet</p>
                  <p className="text-xs mt-1">Use this to backfill historical attendance data</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Check-in</TableHead>
                        <TableHead>Check-out</TableHead>
                        <TableHead>Late</TableHead>
                        <TableHead>Half Day</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {manualAttendance.map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="tabular-nums font-medium">{record.date}</TableCell>
                          <TableCell>{record.employee_name}</TableCell>
                          <TableCell className="tabular-nums">{record.check_in_time_local?.split(' ')[1] || '-'}</TableCell>
                          <TableCell className="tabular-nums">{record.check_out_time ? 'Yes' : '-'}</TableCell>
                          <TableCell>
                            {record.is_late ? (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {record.late_minutes}m
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{record.is_half_day ? <Badge variant="outline" className="text-xs">Yes</Badge> : '-'}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{record.notes || '-'}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAttendance(record.id)} data-testid={`delete-attendance-${record.id}`}>
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEAVES TAB */}
        <TabsContent value="leaves" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {manualLeaves.length} manual leave record{manualLeaves.length !== 1 ? 's' : ''}
            </p>
            <Button onClick={openLeaveDialog} className="gap-2" data-testid="add-manual-leave-button">
              <Plus size={16} /> Add Leave Record
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {manualLeaves.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <FileText className="mx-auto mb-3 text-muted-foreground/50" size={40} />
                  <p>No manual leave records yet</p>
                  <p className="text-xs mt-1">Use this to backfill historical leave data</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {manualLeaves.map(record => (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.employee_name}</TableCell>
                          <TableCell>{record.leave_type_name}</TableCell>
                          <TableCell className="tabular-nums">{record.start_date}</TableCell>
                          <TableCell className="tabular-nums">{record.end_date}</TableCell>
                          <TableCell className="text-right tabular-nums">{record.days}</TableCell>
                          <TableCell className="text-xs max-w-[150px] truncate">{record.reason || '-'}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{record.notes || '-'}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteLeave(record.id)} data-testid={`delete-leave-${record.id}`}>
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Attendance Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Manual Attendance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select value={attendanceForm.employee_id} onValueChange={v => setAttendanceForm({...attendanceForm, employee_id: v})}>
                <SelectTrigger data-testid="manual-att-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {branchEmployees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={attendanceForm.date} onChange={e => setAttendanceForm({...attendanceForm, date: e.target.value})} data-testid="manual-att-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Check-in Time</Label>
                <Input type="time" value={attendanceForm.check_in_time_local} onChange={e => setAttendanceForm({...attendanceForm, check_in_time_local: e.target.value})} data-testid="manual-att-checkin-time" />
              </div>
              <div className="space-y-2">
                <Label>Check-out Time</Label>
                <Input type="time" value={attendanceForm.check_out_time_local} onChange={e => setAttendanceForm({...attendanceForm, check_out_time_local: e.target.value})} data-testid="manual-att-checkout-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Switch checked={attendanceForm.is_late} onCheckedChange={v => setAttendanceForm({...attendanceForm, is_late: v})} />
                <Label>Was Late</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={attendanceForm.is_half_day} onCheckedChange={v => setAttendanceForm({...attendanceForm, is_half_day: v})} />
                <Label>Half Day</Label>
              </div>
            </div>
            {attendanceForm.is_late && (
              <div className="space-y-2">
                <Label>Late Minutes</Label>
                <Input type="number" value={attendanceForm.late_minutes} onChange={e => setAttendanceForm({...attendanceForm, late_minutes: parseInt(e.target.value) || 0})} data-testid="manual-att-late-minutes" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={attendanceForm.notes} onChange={e => setAttendanceForm({...attendanceForm, notes: e.target.value})} placeholder="Optional notes about this entry" rows={2} data-testid="manual-att-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttendanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateAttendance} data-testid="manual-att-save-button">Create Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Leave Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Manual Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select value={leaveForm.employee_id} onValueChange={v => setLeaveForm({...leaveForm, employee_id: v})}>
                <SelectTrigger data-testid="manual-leave-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {branchEmployees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={leaveForm.leave_type_id} onValueChange={v => setLeaveForm({...leaveForm, leave_type_id: v})}>
                <SelectTrigger data-testid="manual-leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map(lt => (
                    <SelectItem key={lt.id} value={lt.id}>{lt.name} ({lt.total_days} days)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} data-testid="manual-leave-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} data-testid="manual-leave-end-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Days</Label>
                <Input type="number" step="0.5" value={leaveForm.days} onChange={e => setLeaveForm({...leaveForm, days: parseFloat(e.target.value) || 1})} data-testid="manual-leave-days" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={leaveForm.is_half_day} onCheckedChange={v => setLeaveForm({...leaveForm, is_half_day: v, days: v ? 0.5 : 1})} />
                <Label>Half Day</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} placeholder="Reason for leave" rows={2} data-testid="manual-leave-reason" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={leaveForm.notes} onChange={e => setLeaveForm({...leaveForm, notes: e.target.value})} placeholder="Admin notes (optional)" rows={2} data-testid="manual-leave-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLeave} data-testid="manual-leave-save-button">Create Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
